<template>
  <section
    v-if="imageItems"
    class="carousel">
    <div
      v-if="showCarouselIndex"
      class="carousel__index">
      <p v-html="`${activeSlide + 1} / ${imageItems.length}`" />
    </div>
    <vue-glide
      v-model="activeSlide"
      v-hammer:swipe.horizontal="swipeImage"
      :options="carouselOptions">
      <vue-glide-slide
        v-for="(imageBlock, imageBlockKey) in imageItems"
        :key="`slide${imageBlockKey}`">
        <div
          v-if="imageBlock.type === 'full'"
          class="container"
          :class="{'container--full': imageBlock.type === 'full'}">
          <span
            v-for="(block, blockKey) in imageBlock.images"
            :key="`item${blockKey}`"
            class="image-block"
            :class="{
              'image-block--single': block.length === 1,
              'image-block--landscape': block.width > block.height,
              'image-block--portrait': block.height > block.width
            }">
            <img :src="block.url">
          </span>
        </div>
        <div
          v-if="imageBlock.type === 'half'"
          class="container"
          :class="{ 'container--half': imageBlock.type === 'half' }">
          <div
            v-for="(block, blockKey) in imageBlock.images"
            :key="`item${blockKey}`">
            <span
              v-for="(image, imageKey) in block"
              :key="`item${imageKey}`"
              class="image-block"
              :class="{
                'image-block--single': block.length === 1,
                'image-block--double': block.length === 2,
                'image-block--landscape': block[imageKey].width > block[imageKey].height,
                'image-block--portrait': block[imageKey].height > block[imageKey].width
              }">
              <img :src="image.url">
            </span>
          </div>
        </div>
      </vue-glide-slide>
    </vue-glide>
    <div
      v-if="imageItems.length > 1"
      class="carousel__interaction">
      <div
        class="carousel__interaction--prev"
        @click="prev()" />
      <div
        class="carousel__interaction--next"
        @click="next()" />
    </div>
    <div class="carousel__captions">
      <p
        class="caption caption--left"
        v-html="captions[activeSlide]" />
    </div>
    <div
      v-if="imageItems.length > 1"
      class="carousel__navigation">
      <ul>
        <li>
          <button @click="prev()">
            Previous
          </button>
        </li>
        <li>
          <button @click="next()">
            Next
          </button>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import { Glide, GlideSlide } from 'vue-glide-js'

export default {
  name: 'Carousel',
  components: {
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide
  },
  props: {
    images: {
      type: Array,
      default: null
    },
    showCarouselIndex: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeSlide: 0,
      windowWidth: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      captions: [],
      carouselOptions: {
        startAt: 0,
        perView: 1,
        gap: (this.windowWidth > 768) ? 0 : 10,
        peek: 0,
        direction: 'ltr',
        focusAt: 0,
        type: 'carousel',
        mode: 'horizontal',
        swipeThreshold: false,
        dragThreshold: false
      },
      scrollEvent: null,
      mousePosition: 0,
      imageItems: null,
      windowResize: null
    }
  },
  computed: {
    mobileItems: function() {
      return this.images.map(image => {
        let imageBlock = {}

        // set block type based on number of images
        imageBlock.type = 'full'

        // add image
        imageBlock.images = [image.image]

        // add caption
        this.captions.push(image.caption)

        return imageBlock
      })
    },
    items: function() {
      let imageBlocks = []

      // map images to imageBlocks
      this.images.map((image, index) => {
        // imageBlock has type and images
        let imageBlock = {}

        // add type
        imageBlock.type = 'full'

        // add image
        imageBlock.images = [image.image]

        imageBlocks.push(imageBlock)

        // add captions to caption
        this.captions.push(image.caption)
      })
      return imageBlocks
    }
  },
  watch: {
    activeSlide: function(slideKey) {
      this.processImageBlockCaptions(slideKey)
    }
  },
  mounted() {
    this.imageItems = (this.windowWidth > 768) ? this.items : this.mobileItems

    this.processImageBlockCaptions(this.activeSlide)

    let resizing = true
    this.windowResize = window.addEventListener('resize', $evt => {
      if (resizing) {
        clearTimeout(resizing)
        resizing = setTimeout(() => {
          // reset image items
          this.imageItems = ((window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) > 768) ? this.items : this.mobileItems

          // reset active slide
          this.activeSlide = 0
        }, 100)
      }
    })

    this.$nextTick()
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResize)
  },
  methods: {
    swipeImage($evt) {
      if ($evt.direction === 2) {
        this.next()
      }
      if ($evt.direction === 4) {
        this.prev()
      }
    },
    prev() {
      // let activeCarouselItems = window.innerWidth || document.documentElement.clientWidth > 768 ? this.items : this.mobileItems

      if (this.activeSlide > 0) {
        this.activeSlide--
      } else {
        this.activeSlide = this.imageItems.length - 1
      }
    },
    next() {
      // let activeCarouselItems = window.innerWidth || document.documentElement.clientWidth > 768 ? this.items : this.mobileItems

      if (this.activeSlide < this.imageItems.length - 1) {
        this.activeSlide++
      } else {
        this.activeSlide = 0
      }
    },
    processImageBlockCaptions(active) {
      // set type and images
      let type = this.imageItems[active].type
      let images = this.imageItems[active].images

      // if full set single caption
      if (type === 'full') {
        this.captions.left = images[0].caption
        this.captions.right = ''
      }

      // if half process left and right side captions
      if (type === 'half') {
        // process left captions
        let captionLeft = (this.windowWidth > 768) ? this.getHalfCaptions(images[0]) : (images[0][0].caption !== '' ? [`Top: ${images[0][0].caption}`] : null)
        // process right captions
        let captionRight = (this.windowWidth > 768) ? this.getHalfCaptions(images[1]) : (images[1][0].caption !== '' ? [`Bottom: ${images[1][0].caption}`] : null)

        // add captions
        this.captions.left = captionLeft ? captionLeft.join('<br>') : ''
        this.captions.right = captionRight ? captionRight.join('<br>') : ''
      }
    },
    getHalfCaptions(images) {
      if (images) {
        return images.reduce((captions, image, index) => {
          // if there are multiple captions per block add Top & Bottom
          let caption = images.length === 1 ? image.caption :
            (index === 0 ? `Top: ${image.caption}` : `Bottom: ${image.caption}`)

          captions = typeof captions !== 'undefined' ? captions : []

          // return caption if it isn't blank
          if (image.caption !== '') return [...captions, caption]
        }, new Array)
      }
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
$carouselHeight: calc(50vw * 1.2);
$carouselMinHeight: calc(50vw * 1.2);
.carousel {
  // min-height:$carouselHeight;
  position:relative;
  .glide {
    height:calc(50vw * 1.2);
    max-height: $carouselHeight;
    // min-height:$carouselMinHeight;
    &__slide {
      width:100%;
      // height: $carouselHeight;
      // min-height: $carouselMinHeight;
      cursor:pointer;

      .container {
        width:100%;
        height:100%;
        display:flex;
        // min-height:$carouselMinHeight;
        &--full {
          div {
            img {
              width:100%;
            }
          }
        }
        &--half {
          div {
            width:50%;
            display:flex;
            flex-direction: column;
            &:first-child {
              margin-right: $padding;
            }
            .image-block--single.image-block--landscape {
              img {
                object-fit: contain;
                object-position: 0 0;
              }
            }
          }
        }
        .image-block {
          display:inline-block;
          width:100%;
          background-color: $grey;
          &--single {
            height: calc(50vw * 1.2);
            max-height: $carouselHeight;
            width:100%;
          }
          &--double {
            height:calc(40vh - (#{$padding} / 2));
            &:first-child {
              margin-bottom: $padding;
            }
            .image-block--landscape {
              img {
                height: calc(40vh - (#{$padding} / 2)) !important;
              }
            }
          }
          &--landscape {

            &.image-block--single {
              height:100%;
            }
          }
          img {
            width:100%;
            height:100%;
            object-fit:cover;
            object-position: 50% 50%;
          }
          @include desktop {
            img {
              object-fit: contain;
              object-position: 0 0;
            }
          }
        }
      }
    }
  }

  @include desktop {
    $carouselHeight: 80vh;
    $carouselMinHeight: 80vh;
    .glide {
      height:80vh;
      max-height: 80vh;

      &__slide {

        .container {
          &--half {
            div {
              .image-block--single.image-block--landscape {
                img {
                  object-fit: contain;
                  object-position: 0 0;
                }
              }
            }
          }
          .image-block {
            &--single {
              height: 80vh;
              max-height: 80vh;
            }
            &--double {
              height:calc(40vh - (#{$padding} / 2));
              &:first-child {
                margin-bottom: $padding;
              }
              .image-block--landscape {
                img {
                  height:calc(40vh - (#{$padding} / 2)) !important;
                }
              }
            }
            &--landscape {

              &.image-block--single {
                height:100%;
              }
            }
            img {
              width:100%;
              height:100%;
              object-fit:cover;
              object-position: 50% 50%;
            }
            @include desktop {
              img {
                object-fit: contain;
                object-position: 0 0;
              }
            }
          }
        }
      }
    }

  }

  &__index {
    position:absolute;
    right:0px;
    top: -33px
  }


  &__interaction {
    position: absolute;
    top:0px;
    left:0px;
    width:100%;
    opacity:0;
    height:calc(50vw * 1.2);
    // $carouselHeight: 80vh;
    max-height: $carouselHeight;
    @include desktop {
      height:80vh;
      // $carouselHeight: 80vh;
      max-height:80vh;
    }
    display:flex;
    &--prev {
      width:50%;
      cursor: w-resize;
    }
    &--next {
      width:50%;
      cursor: e-resize;
    }
  }

  &__captions {
    display:flex;
    margin: $padding 0 0 0;
    min-height: 2rem;
  }

  &__navigation {
    padding:0 0 $padding 0;
    ul {
      display:flex;
      li {
        width:calc(50% - (#{$padding} / 2));
        margin:0 $padding 0 0;

        &:last-child {
          margin:0;
        }
      }
    }
    button {
      @include button_resets;
      font-family: $suisse;
      font-size: $body;
      cursor:pointer;
    }
  }

  @media screen and (min-width: $mobile) and (max-width: $desktop) {
    .glide {
      &__slide {
        .container {
          .image-block {
            height: calc(50vw * 1.2);
          }
        }
      }
    }

    &__interaction {
      height: calc(50vw * 1.2);
    }
  }

  @include mobile {
    $carouselMinHeight: 450px;
    display:flex;
    flex-wrap:wrap;
    .glide {
      height:calc((100vw - #{$padding * 2}) * 1.2);
      min-height: $carouselMinHeight;
      order:1;
      &__slides {
        max-height: calc((100vw - 20px) * 1.2);
        min-height: $carouselMinHeight;
        height: unset !important;
      }
      &__slide {
        height: calc((100vw - 20px) * 1.2);
        min-height: $carouselMinHeight;

        .container {
          flex-wrap:wrap;
          &--half {
            div {
              width:100%;
              height: calc((100vw - 20px) * 1.2 / 2 - 6px);
              min-height: calc(#{$carouselMinHeight / 2} - 6px);
              margin-right:0px;
              &:first-child {
                margin:0 0 $padding 0 !important;
              }

              .image-block {
                height: calc((100vw - 20px) * 1.2 / 2 - 6px);

                &--portrait {
                  img {
                    object-fit: contain;
                  }
                }

                &--landscape {
                  img {
                    object-fit: cover;
                  }
                }
              }
            }
          }
          &--full {
            .image-block {
              min-height:$carouselMinHeight;

              &--landscape {
                img {
                  object-fit: contain;
                  object-position:0% 0%;
                }
              }
            }
          }
        }
      }
    }

    &__captions {
      width:100%;
      flex-wrap:wrap;
      order:3;
      margin:0;
      p {
        width:100%;
      }
    }

    &__navigation {
      order:2;
      width:100%;
      padding:0;
      margin: $padding 0;
      button {
        font-size: $body--m;
        line-height: $line-height;
      }
    }

    &__index {
      // position:relative;
      top:-22px;
    }

    &__interaction {
      // height: calc(50vw * 1.2);
      display:none;
    }
  }
}
</style>

<style lang="scss">
.carousel {
  @import "node_modules/@glidejs/glide/src/assets/sass/glide.core";

  .glide {
    &__slides {
      height:calc(50vw * 1.2);

    }
  }

  @include desktop {
    .glide {
      &__slides {
        height:80vh;
      }
    }
  }

  @include mobile {
    .glide {
      overflow:hidden;
      &__slides {
        height:calc((100vw - 20px) * 1.2);
        min-height:450px;
        max-height: unset;
      }
    }
  }
}
</style>
